import { Separator } from '@/components/ui/separator';
import { TaskCommand, useTaskById, useTodaysBoxTasks } from '@/lib/api/task';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useBoxes } from '@/lib/api/boxes';
import { useLiveIps } from '@/lib/api/liveBoxes';
import { boxActions } from '@/pages/boxes-list/box-actions';
import { TaskExecution } from './task-execution';
import { TaskInfo } from './task-info';
import { useMemo } from 'react';
import { format } from 'date-fns';

type TaskParam = {
  taskId: string;
};

export function TaskRunner() {
  const location = useLocation();
  const navigate = useNavigate();
  const { taskId } = useParams<TaskParam>();
  const [value] = useTaskById(taskId);
  const [boxes] = useBoxes();
  const [todaysTasks] = useTodaysBoxTasks();
  const liveIps = useLiveIps();

  const isBoxes = location.pathname.includes('/boxes');

  const boxNames = useMemo(() => {
    const map: Record<string, string> = {};
    boxes?.forEach((box) => {
      if (box?.ip) map[box.ip] = box.name;
    });
    return map;
  }, [boxes]);

  return (
    <>
      <Breadcrumb className="pb-2">
        <BreadcrumbList>
          {isBoxes ? (
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/boxes">Box</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
          ) : (
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/tv-controllers">TV Controller</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
          )}
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>Task</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="text-3xl font-bold">Task Monitor</h1>
        {isBoxes && (
          <Select
            value={taskId}
            onValueChange={(id) => navigate(`/boxes/task/${id}`)}
          >
            <SelectTrigger className="w-[280px]">
              <SelectValue placeholder="Previous tasks (today)" />
            </SelectTrigger>
            <SelectContent>
              {todaysTasks
                ?.filter((task) => task.task_command !== TaskCommand.KeyEvent)
                .map((task) => (
                  <SelectItem key={task.id} value={task.id!}>
                    {task.task_initiated_date
                      ? `${format(task.task_initiated_date, 'HH:mm')} · `
                      : ''}
                    {task.task_command} · {task.boxes?.length ?? 0} box
                    {(task.boxes?.length ?? 0) === 1 ? '' : 'es'}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        )}
      </div>
      <Separator className="my-6" />
      <div className="grid lg:grid-cols-4 grid-cols-1 gap-8">
        <TaskInfo task={value} boxNames={isBoxes ? boxNames : {}} />
        <div className="lg:col-span-3 col-span-1">
          {value?.boxes?.map((ip: string) => (
            <TaskExecution
              key={ip}
              ip={ip}
              name={isBoxes ? boxNames[ip] : undefined}
              initialTaskId={taskId!}
              actions={isBoxes ? boxActions : undefined}
              isTvControllerTask={!isBoxes}
              locked={isBoxes && liveIps.has(ip)}
            />
          ))}
        </div>
      </div>
    </>
  );
}
